import { reportError } from '@ms/yammer-telemetry';
import { combinePath } from '@ms/yammer-web-support/dist/location';
import { matchPath } from 'react-router-dom';
import { createStore } from 'zustand/vanilla';

import { AppRoutes, PageRoute, ParentPageRoute } from './AppPageRoutesWithTelemetry.types';
import { isParentRoute } from './isParentRoute';

interface RoutesStore {
  readonly routes: AppRoutes | undefined;
  readonly setRoutes: (routes: AppRoutes) => void;
}
const routesStore = createStore<RoutesStore>((set) => ({
  routes: undefined,
  setRoutes: (routes: AppRoutes) => set({ routes }),
}));

export const setRoutesForAssetPreloader = (routes: AppRoutes) => routesStore.getState().setRoutes(routes);
const getRoutes = () => routesStore.getState().routes;

type PreloadPageAssets = (path: string | undefined) => void;

const hasMatch = (path: string, route: PageRoute | ParentPageRoute) =>
  !!matchPath(path, { path: route.path, exact: (route as PageRoute).exact });

/**
 * Preloads page and subpage JS bundles. `path` can be an actual path with real
 * ids (`/users/REAL_USER_ID/storyline`) or it can be a route config path with
 * id placeholders (`/users/:userId/storyline`).
 *
 * A common use case is to invoke this on a link's `onHover` and `onFocus` events
 * to begin preloading the next page's assets before the user clicks.
 */
export const preloadPageAssets: PreloadPageAssets = (path) => {
  const routes = getRoutes();
  if (!routes) {
    reportError({ error: 'preloadPageAssets called without routes set' });

    return;
  }

  if (!path) {
    return;
  }

  routes.some((route) => {
    if (hasMatch(path, route)) {
      route.loader();

      if (isParentRoute(route)) {
        route.childRoutes.some((childRoute) => {
          if (hasMatch(path, { ...childRoute, path: combinePath(route.path, childRoute.path) })) {
            childRoute.loader();

            return true;
          }
        });
      }

      return true;
    }
  });
};
